import { ExtensionCategories } from 'lumapps-sdk-js';

import BaseApi, { makeParamsSerializer, ServerListRequest, ServerListResponse } from '@lumapps/base-api';

import { ExtensionStatus, ExtensionTopics } from '../constants';
import type { InstalledExtension } from '../types';

export interface InstalledExtensionListFilters {
    // Filter by extension categories
    categories?: ExtensionCategories[];
    // Filter whether the installed extensions are up to date
    isUpToDate?: boolean;
    // Filter by installation date (after)
    installedAfter?: string;
    // Filter by installation date (before)
    installedBefore?: string;
    // Filter by partner id
    partnerId?: string;
    // Search term
    search?: string;
    // Filter by extension status
    statuses?: (keyof typeof ExtensionStatus)[];
    // Filter by topics
    topics?: (keyof typeof ExtensionTopics)[];
}

export const installedExtensionsQueryKeys = {
    all: ['marketplace', 'installed-extensions'] as const,
    list: (filters: InstalledExtensionListFilters) => [...installedExtensionsQueryKeys.all, filters] as const,
    item: (id?: string) => [...installedExtensionsQueryKeys.all, id] as const,
};

/**
 * This API instance is dedicated to managing installed extensions for a specific organization.
 */
export const organizationInstalledExtensionsApi = new BaseApi({
    path: 'installed-extensions',
    version: BaseApi.versions.v2,
});

// todo: check language headers
/**
 * Retrieve an installed extension.
 *
 * @see {@link https://api-doc.lumapps.net/#operation/v2.installed_extensions.get}
 */
export const fetchInstalledExtension = (id: string) =>
    organizationInstalledExtensionsApi
        .get<InstalledExtension>(
            `/${id}`,
            {
                // headers: { ...languageHeader },
            },
            true,
            true,
        )
        .then((response) => response.data);

export interface InstalledExtensionListAPIParams extends InstalledExtensionListFilters, ServerListRequest {}

/**
 * Load a paginated list of installed extensions.
 *
 * @param params - Parameters for filtering and paginating the extension list.
 * @param [signal] - Optional AbortSignal to abort the request if needed.
 *
 * @see {@link https://api-doc.lumapps.net/#operation/v2.installed_extensions.list_installed}
 */
export const fetchInstalledExtensionList = (params: InstalledExtensionListAPIParams, signal?: AbortSignal) =>
    organizationInstalledExtensionsApi
        .get<ServerListResponse<InstalledExtension>>(
            '',
            {
                headers: {
                    // ...languageHeader,
                },
                params,
                paramsSerializer: makeParamsSerializer({ arrayFormat: 'comma', encode: false }),
                signal,
            },
            true,
            true,
        )
        .then((results) => results.data);

/**
 * Install an extension from the marketplace to the platform.
 *
 * @param extensionId - The ID of the extension to be installed.
 *
 * @see {@link https://api-doc.lumapps.net/#operation/v2.installed_extensions.install}
 */
export const installExtension = (extensionId: string) =>
    organizationInstalledExtensionsApi
        .post<InstalledExtension>('', {
            extensionId,
        })
        .then((response) => response.data);

/**
 * Upgrade an extension.
 *
 * @param extensionId - The ID of the extension to be upgraded.
 *
 * @see {@link https://api-doc.lumapps.net/#operation/v2.installed_extensions.upgrade}
 */
export const upgradeExtension = (extensionId: string) =>
    organizationInstalledExtensionsApi.put<InstalledExtension>(`${extensionId}/version`);

export type UpdateExtensionParams = Pick<
    InstalledExtension,
    | 'configuration'
    | 'connectorIds'
    | 'status'
    | 'allowedSiteIds'
    | 'isAllowedBySites'
    | 'isDebugModeEnabled'
    | 'visibleByGroupIds'
>;

/**
 * Update an installed extension.
 * NOTE: Not to be confused with Upgrade.
 * This one allows configuration and connectors to be updated.
 *
 * @param extensionId - The ID of the extension to be updated.
 * @param params - Parameters for updating the extension.
 *
 * @see {@link https://api-doc.lumapps.net/#operation/v2.installed_extensions.update}
 */
export const updateExtension = (extensionId: string, params: UpdateExtensionParams) =>
    organizationInstalledExtensionsApi
        .put<InstalledExtension>(`${extensionId}`, params)
        .then((response) => response.data);

/**
 * Uninstall an extension from the platform.
 *
 * @param extensionId - The ID of the extension to be uninstalled.
 *
 * @see {@link https://api-doc.lumapps.net/#operation/v2.installed_extensions.uninstall}
 */
export const uninstallExtension = (extensionId: string) =>
    organizationInstalledExtensionsApi.delete<undefined>(extensionId);
